import { env } from '@/env';
import { useGetFeaturedAvatarQuery } from '@/services/avatarApi';

/**
 * The player's QAvatar, resolved once and shared.
 *
 * The AI-generated avatar IS the player's official picture — the Laravel
 * `avatarUrl` circle it replaced is legacy and no longer rendered anywhere.
 *
 * Two callers today: `<BannerAvatar />` paints it over the app banner on every
 * screen, and the Hub asks `hasFloating` to decide whether to offer its
 * "create an avatar" fallbacks. They share one RTK Query cache entry, so the
 * second caller costs nothing.
 *
 * `hasFloating` is false when the avatar has no transparency: an opaque
 * full-frame image over the banner would just read as a pasted rectangle.
 * There is no placeholder yet — when the player has nothing to show, the
 * banner simply carries the name (a dragon-egg placeholder comes later).
 */
export function useFeaturedAvatar() {
  // Skipped entirely when avatars are off (PROD): `featured` stays undefined
  // and every branch below collapses without extra gating.
  const { data: featured } = useGetFeaturedAvatarQuery(undefined, {
    skip: !env.featureAvatarsEnabled,
  });

  // While a mesh transition is in flight, the 2D avatar steps aside so
  // <FeaturedAvatar /> can own the display (rotating GLB or status copy).
  const meshStatus = featured?.mesh?.status;
  const meshInFlight = meshStatus !== undefined && meshStatus !== 'none' && meshStatus !== 'failed';

  const clip = featured?.videoClips?.find((c) => c.clipId === featured.activeVideoClipId) ?? null;
  const clipReady = !!clip && clip.status === 'ready' && !!clip.videoUrl;
  const clipHasAlpha = clipReady && !!clip.transparency && clip.transparency !== 'none';

  // An animated transparent clip beats the static PNG when we have one.
  const showVideo = clipHasAlpha && !meshInFlight;
  const showImage = !showVideo && !!featured?.imageUrl && !!featured?.hasAlpha && !meshInFlight;

  return {
    featured,
    clip,
    showVideo,
    showImage,
    /** True when there's a transparent avatar to float over the banner. */
    hasFloating: showVideo || showImage,
  };
}
